import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  TipoComprobante,
  TiposComprobantePagingRes,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchTipoComprobantes = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetTipoComprobantesParams> = {}) => {
  return useQuery({
    queryKey: ['tipo-comprobantes', ...Object.values(params ?? {})],
    queryFn: () => getTipoComprobantes(params),
    enabled: enabled,
  });
};

export const useGetTipoComprobante = (id: string) => {
  return useQuery({
    queryKey: ['tipo-comprobante', id],
    queryFn: () => getTipoComprobante(id),
    retry: false,
  });
};

export const useCreateTipoComprobante = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createTipoComprobante,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipo-comprobantes'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'TipoComprobante creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateTipoComprobante = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateTipoComprobante,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipo-comprobantes'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'TipoComprobante actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeleteTipoComprobante = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: deleteTipoComprobante,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipo-comprobantes'] });
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetTipoComprobantesParams = Partial<TipoComprobante> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};
export type CreateTipoComprobanteParams = Omit<
  TipoComprobante,
  'id_tipocomprobante'
>;
export type UpdateTipoComprobanteParams = {
  id: string;
  data: CreateTipoComprobanteParams;
};

export const getTipoComprobantes = async (
  params?: GetTipoComprobantesParams
) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<TiposComprobantePagingRes>(
    `${urlBackend}api/v1/tipocomprobante/?${queryParams}`,
    true
  );
};

export const getTipoComprobante = async (id: string) => {
  try {
    return await get<TipoComprobante>(
      `${urlBackend}api/v1/tipocomprobante/${id}`,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createTipoComprobante = async (
  data: CreateTipoComprobanteParams
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<TipoComprobante>(
    `${urlBackend}api/v1/tipocomprobante/`,
    data,
    true
  );
};

export const updateTipoComprobante = async ({
  id,
  data,
}: UpdateTipoComprobanteParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<TipoComprobante>(
    `${urlBackend}api/v1/tipocomprobante/${id}/`,
    data,
    true
  );
};

export const deleteTipoComprobante = async (id: number) => {
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  const res = await remove<TipoComprobante>(
    `${urlBackend}api/v1/tipocomprobante/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
